import { notFound } from "@tanstack/react-router";
import { buildHref, matchRoute } from "@softmax/web-addresses";
import { leavesTheSite } from "@softmax/web-features/site/records";
import type { SiteSearchCoworldContext } from "@softmax/web-features/site/records";
import {
  resolvePublicCoworld,
  requireGameContext,
  requireSiteAdmission,
} from "@/features/game/data";
import { curatedDocs } from "@/features/preview/records";
export async function loadSearchPage({
  signal,
  href,
}: {
  signal?: AbortSignal | undefined;
  href: string;
}) {
  const url = new URL(href, window.location.origin);

  if (url.origin !== window.location.origin) throw notFound();

  const query = url.searchParams.get("q") ?? "";
  const route = matchRoute(url.pathname);

  if (route.name === "search") {
    await requireSiteAdmission({ href, game: null, signal });
    return { query };
  }

  if (route.name !== "game.search") throw notFound();

  const context = await requireGameContext({ href, signal });
  const resolution = await resolvePublicCoworld({
    signal,
    publicAddress: buildHref({ name: "game.home", game: context.address }),
    context,
  });

  if (resolution.status === "not-found") throw notFound();

  if (resolution.status === "unavailable") {
    if (!context.coworldName) throw new Error("Game search is unavailable");

    return {
      query,
      coworld: {
        name: context.coworldName,
        corpusSlug: context.corpusSlug,
        route: { game: context.address },
        hasWiki: false,
      } satisfies SiteSearchCoworldContext,
    };
  }

  const docs = curatedDocs({
    slug: resolution.slug,
    game: resolution.route.game,
  });
  return {
    query,
    coworld: {
      name: resolution.coworldName,
      corpusSlug: resolution.slug,
      route: resolution.route,
      hasWiki: !docs || !leavesTheSite(docs.href),
    } satisfies SiteSearchCoworldContext,
  };
}
